import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { Alert, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';

import WorkspaceInput from '@components/Common/WorkspaceInput';

import { type FieldDiff, computeFieldDiffs } from './structured/revisionDiff';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** The body as it stood BEFORE this turn was applied, so any single edit can be undone. */
  previousContentJson?: string;
  lostContent?: boolean;
  wordsBefore?: number;
  wordsAfter?: number;
}

/** A finished revision the operator has not yet decided to keep. */
interface Pending {
  instruction: string;
  previousContentJson: string;
  nextContentJson: string;
  diffs: FieldDiff[];
  note: string;
  revertedFields: string[];
  problems: string[];
  lostContent: boolean;
  wordsBefore?: number;
  wordsAfter?: number;
}

interface StructuredRevisionChatProps {
  /** The complete template envelope, as the editor holds it. */
  contentJson: string;
  pageType: string;
  /**
   * Top-level keys the edit may touch; the backend reverts anything that changes outside them.
   * Empty means a whole-guide edit.
   */
  scope?: string[];
  /** Applies the confirmed, complete new envelope to the editor. Nothing is saved by this. */
  onRevised: (contentJson: string) => void;
  /** Names the section when the chat is scoped to one, for the opener and the composer. */
  scopeLabel?: string;
}

/** Whole-guide openers; a blank box invites nothing and the vocabulary is not obvious. */
const WHOLE_SUGGESTIONS = [
  'Tighten the introduction to two sentences',
  'Make the tone more direct throughout',
  'Add a key takeaway about salary',
  'Remove repetition between sections',
];

/**
 * Editing a template guide by asking, rather than by re-importing it.
 *
 * The structured sibling of `RevisionChat`. That one is HTML in, HTML out, and applies the result
 * straight to the editor beside a live preview. This one sends the template `contentJson` and gets
 * the whole edited envelope back, and a template has no live preview, so applying blind would let a
 * reworded paragraph or a reshuffled table land with nothing on screen to show it.
 *
 * **So the result is shown as a field-level diff and confirmed before it is applied.** The panel
 * lists each changed top-level field with its before and after (prose as text, lists and tables as
 * the rows added and removed), the note the model wrote, any fields it reverted because they fell
 * outside the scope, and the same lost-content warning the HTML chat uses. Only a confirm writes
 * the new body into the editor, and even then nothing reaches the database until the shell Save:
 * undo on a kept turn restores the body as it stood before it, exactly as the HTML chat does.
 *
 * `scope` drives the backend: a section affordance opens this pre-scoped to that section's key, so
 * an edit that strays into another field is reverted rather than silently kept. An empty scope is a
 * deliberate whole-guide edit for cross-cutting changes.
 */
const StructuredRevisionChat: React.FC<StructuredRevisionChatProps> = ({
  contentJson,
  pageType,
  scope = [],
  onRevised,
  scopeLabel,
}) => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);

  const scoped = scope.length > 0;

  const send = async (instruction: string) => {
    const text = instruction.trim();
    if (!text || busy || pending) return;

    setBusy(true);
    setElapsed(0);
    setError('');
    setDraft('');
    const before = contentJson;
    setTurns((t) => [...t, { role: 'user', content: text }]);

    try {
      /*
       * Read the stream rather than awaiting a response, for the reason /revise does: a structured
       * edit re-emits the whole envelope and costs about what generating the guide cost, so a plain
       * POST comes back 504 from the proxy while the model is still writing. The elapsed count
       * comes from the heartbeat, so the number on screen is real rather than a spinner pretending.
       */
      const res = await fetch('/api/guides/revise-structured', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentJson: before,
          pageType,
          instruction: text,
          scope,
          // Only enough for "make it shorter" to know what "it" was: the body is the real context.
          history: turns.map((t) => ({ role: t.role, content: t.content })),
        }),
      });
      if (!res.ok || !res.body) {
        const failed = await res.json().catch(() => null);
        throw new Error(failed?.error || 'Could not edit the guide');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let data: any = null;
      let streamError = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const evt = JSON.parse(line);
          if (evt.stage === 'done') data = evt.revision;
          else if (evt.stage === 'error') streamError = evt.error;
          else if (evt.stage === 'working') setElapsed(evt.seconds ?? 0);
        }
      }
      if (streamError) throw new Error(streamError);
      if (!data?.contentJson) throw new Error('The edit returned nothing to apply');

      // Normalise to the same pretty-printed shape the editor keeps, so the JSON fallback stays
      // readable and a save is not a whitespace-only diff.
      let nextJson = data.contentJson;
      try {
        nextJson = JSON.stringify(JSON.parse(data.contentJson), null, 2);
      } catch {
        /* keep the raw string; parseGuide in the panel will report an invalid body */
      }

      const changedFields: string[] = Array.isArray(data.changedFields) ? data.changedFields : [];
      const diffs = computeFieldDiffs(before, nextJson, changedFields, pageType);

      // The edit is NOT applied here: it waits in `pending` for the operator to confirm the diff.
      setPending({
        instruction: text,
        previousContentJson: before,
        nextContentJson: nextJson,
        diffs,
        note: data.note || 'Proposed an edit.',
        revertedFields: Array.isArray(data.revertedFields) ? data.revertedFields : [],
        problems: Array.isArray(data.problems) ? data.problems : [],
        lostContent: !!data.lostContent,
        wordsBefore: data.wordsBefore,
        wordsAfter: data.wordsAfter,
      });
    } catch (e: any) {
      // A fetch, so the reason arrives as e.message (the route's own sentence, such as the budget
      // refusal). The user turn is rolled back so the instruction can be tried again cleanly.
      setError(e?.response?.data?.error || e?.message || 'Could not edit the guide');
      setTurns((t) => t.slice(0, -1));
    } finally {
      setBusy(false);
      requestAnimationFrame(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
      });
    }
  };

  /** Keep the pending revision: write it into the editor and log it as an undoable turn. */
  const apply = () => {
    if (!pending) return;
    onRevised(pending.nextContentJson);
    setTurns((t) => [
      ...t,
      {
        role: 'assistant',
        content: pending.note,
        previousContentJson: pending.previousContentJson,
        lostContent: pending.lostContent,
        wordsBefore: pending.wordsBefore,
        wordsAfter: pending.wordsAfter,
      },
    ]);
    if (pending.problems.length > 0) {
      setError(`The edit still breaks a house rule: ${pending.problems[0]}`);
    }
    setPending(null);
  };

  /** Drop the pending revision: the editor is untouched, and the turn is logged as not applied. */
  const discard = () => {
    if (!pending) return;
    setTurns((t) => [...t, { role: 'assistant', content: `${pending.note} (discarded)` }]);
    setPending(null);
  };

  const undo = (turn: Turn, index: number) => {
    if (turn.previousContentJson === undefined) return;
    onRevised(turn.previousContentJson);
    // The turn stays in the log, marked, rather than disappearing: a chat that erases its own
    // history is one you cannot reason about.
    setTurns((t) =>
      t.map((entry, i) =>
        i === index
          ? { ...entry, content: `${entry.content} (undone)`, previousContentJson: undefined }
          : entry,
      ),
    );
  };

  return (
    <Box className="ge-chat ge-struct-chat" data-scope={scoped ? scope.join(',') : 'whole-guide'}>
      <Box className="ge-chat__log" ref={scroller}>
        {turns.length === 0 && !pending && (
          <Box className="ge-chat__empty">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {scoped
                ? `Ask for a change to ${scopeLabel ?? 'this section'}. The edit is kept to it, and nothing is applied until you confirm the diff.`
                : 'Ask for a change to the whole guide. You see a field-by-field diff and confirm it before anything is applied, and nothing is saved until you save.'}
            </Typography>
            {!scoped &&
              WHOLE_SUGGESTIONS.map((s) => (
                <Button
                  key={s}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 0.75, mb: 0.75 }}
                  onClick={() => send(s)}
                >
                  {s}
                </Button>
              ))}
          </Box>
        )}

        {turns.map((turn, i) => (
          <Box key={i} className={`ge-chat__turn ge-chat__turn--${turn.role}`}>
            <Typography variant="body2">{turn.content}</Typography>
            {turn.role === 'assistant' && (
              <Box className="ge-chat__meta">
                {turn.lostContent && (
                  <Typography variant="caption" className="ge-chat__warning">
                    This edit dropped text: {turn.wordsBefore} words became {turn.wordsAfter}. Check
                    what went before keeping it.
                  </Typography>
                )}
                {turn.previousContentJson !== undefined && (
                  <IconButton
                    size="small"
                    aria-label="Undo this edit"
                    title="Undo this edit"
                    onClick={() => undo(turn, i)}
                  >
                    <UndoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        ))}

        {busy && (
          <Box className="ge-chat__busy">
            <CircularProgress size={16} />
            <Typography variant="caption">
              {/* A real number from the heartbeat: a structured edit rewrites the whole envelope,
                  so this is minutes rather than seconds and a silent spinner would read as a hang. */}
              Editing the guide{elapsed > 0 ? ` (${elapsed}s)` : ''}...
            </Typography>
          </Box>
        )}
      </Box>

      {/* The confirm step: the result is shown here and applied only on confirm. */}
      {pending && (
        <Box className="ge-diff" role="group" aria-label="Review the proposed edit">
          <Typography className="ge-diff__title" component="h4">
            {pending.diffs.length === 0
              ? 'No fields changed'
              : `Review ${pending.diffs.length} changed field${pending.diffs.length === 1 ? '' : 's'}`}
          </Typography>
          <Typography variant="body2" className="ge-diff__note">
            {pending.note}
          </Typography>

          {pending.lostContent && (
            <Alert severity="error" className="ge-diff__lost">
              This edit dropped text: {pending.wordsBefore} words became {pending.wordsAfter}. Check
              what went before keeping it.
            </Alert>
          )}

          {pending.revertedFields.length > 0 && (
            <Alert severity="info" className="ge-diff__reverted">
              Reverted, because it changed outside the section you asked to edit:{' '}
              {pending.revertedFields.join(', ')}.
            </Alert>
          )}

          {pending.diffs.length > 0 && (
            <Box className="ge-diff__fields">
              {pending.diffs.map((d) => (
                <Box key={d.key} className="ge-diff__field" data-field={d.key}>
                  <Typography className="ge-diff__field-label">{d.label}</Typography>
                  {d.kind === 'text' ? (
                    <Box className="ge-diff__cols">
                      <Box className="ge-diff__col ge-diff__col--before">
                        <Typography variant="caption" className="ge-diff__col-head">
                          Before
                        </Typography>
                        <Typography variant="body2" className="ge-diff__text">
                          {d.before || '(empty)'}
                        </Typography>
                      </Box>
                      <Box className="ge-diff__col ge-diff__col--after">
                        <Typography variant="caption" className="ge-diff__col-head">
                          After
                        </Typography>
                        <Typography variant="body2" className="ge-diff__text">
                          {d.after || '(empty)'}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box className="ge-diff__rows">
                      {d.removed.length === 0 && d.added.length === 0 && (
                        <Typography variant="body2" className="ge-diff__text">
                          Rows reordered or reworded in place.
                        </Typography>
                      )}
                      {d.removed.map((row, j) => (
                        <Typography key={`r${j}`} variant="body2" className="ge-diff__row ge-diff__row--removed">
                          - {row}
                        </Typography>
                      ))}
                      {d.added.map((row, j) => (
                        <Typography key={`a${j}`} variant="body2" className="ge-diff__row ge-diff__row--added">
                          + {row}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box className="ge-diff__actions">
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckOutlinedIcon />}
              onClick={apply}
            >
              Apply to editor
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseOutlinedIcon />}
              onClick={discard}
            >
              Discard
            </Button>
          </Box>
          <Typography variant="caption" className="ge-diff__foot">
            Applying writes this into the editor. Nothing is saved until you save.
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box className="ge-chat__composer">
        <WorkspaceInput
          value={draft}
          placeholder={
            scoped ? `Ask for a change to ${scopeLabel ?? 'this section'}` : 'Ask for a change to this guide'
          }
          disabled={busy || !!pending}
          onChange={setDraft}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || (e as React.KeyboardEvent).shiftKey) return;
            e.preventDefault();
            send(draft);
          }}
        />
        <IconButton
          aria-label="Send"
          color="primary"
          disabled={busy || !!pending || !draft.trim()}
          onClick={() => send(draft)}
        >
          <SendOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default StructuredRevisionChat;
